import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, FileText, Lock, Shield, Server, CheckCircle, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface DocumentUploadStepProps {
  orgId: string;
  familyId: string;
  onComplete: () => void;
  onSkip: () => void;
}

type DocCategory = 'banking' | 'holdings' | 'memos';

interface UploadFile {
  file: File;
  category: DocCategory;
  uploading: boolean;
  progress: number;
}

export default function DocumentUploadStep({ orgId, familyId, onComplete, onSkip }: DocumentUploadStepProps) {
  const navigate = useNavigate();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [clientId, setClientId] = useState<string | null>(null);

  // Resolve the client_id for this family on mount
  useEffect(() => {
    supabase
      .from('clients')
      .select('id')
      .eq('family_id', familyId)
      .limit(1)
      .then(({ data }) => {
        if (data && data.length > 0) {
          setClientId(data[0].id);
        }
      });
  }, [familyId]);

  const handleFileSelect = (category: DocCategory) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files).map(file => ({
      file,
      category,
      uploading: false,
      progress: 0
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      toast.error("Please select at least one file to upload");
      return;
    }

    setUploading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      for (let i = 0; i < files.length; i++) {
        const uploadFile = files[i];
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, uploading: true, progress: 0 } : f
        ));

        const fileExt = uploadFile.file.name.split('.').pop();
        const fileName = `${orgId}/${Date.now()}_${uploadFile.file.name}`;
        
        const { data: storageData, error: storageError } = await supabase.storage
          .from('documents')
          .upload(fileName, uploadFile.file);

        if (storageError) throw storageError;

        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, progress: 50 } : f
        ));

        const { data: { publicUrl } } = supabase.storage
          .from('documents')
          .getPublicUrl(fileName);

        const docType = uploadFile.category === 'banking' ? 'statement' 
          : uploadFile.category === 'holdings' ? 'holdings'
          : 'memo';

        const { error: docError } = await supabase
          .from('documents')
          .insert({
            org_id: orgId,
            family_id: familyId,
            client_id: clientId || familyId,
            type: docType,
            storage_url: publicUrl,
            sha256: fileName,
            source: 'onboarding_upload',
            metadata: { original_name: uploadFile.file.name }
          });

        if (docError) throw docError;

        setFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, progress: 100 } : f
        ));
      }

      setUploadedCount(files.length);
      setUploadComplete(true);
      setUploading(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload documents');
      setUploading(false);
    }
  };

  // Show completion screen after successful upload
  if (uploadComplete) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold">Documents Uploaded!</h3>
          <p className="text-sm text-muted-foreground">
            {uploadedCount} document{uploadedCount !== 1 ? 's' : ''} uploaded successfully.
          </p>
        </div>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Server className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-sm">Queued for Processing</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your documents are now queued for automated data extraction.
                  You can track the extraction progress and review extracted data
                  in the Data Ingestion page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-3 pt-2">
          <Button onClick={onComplete} className="w-full">
            Continue to Dashboard
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/data/ingestion')}
            className="w-full"
          >
            Go to Data Ingestion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Upload Your Documents</h3>
        <p className="text-sm text-muted-foreground">
          Help us establish your portfolio's source of truth by uploading key documents
        </p>
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6 space-y-3">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-primary mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-sm">End-to-End Encryption</p>
              <p className="text-xs text-muted-foreground">All documents are encrypted at rest and in transit</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-primary mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-sm">Privacy First</p>
              <p className="text-xs text-muted-foreground">Your data is never sent to external AI models</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Server className="h-5 w-5 text-primary mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-sm">Secure Infrastructure</p>
              <p className="text-xs text-muted-foreground">Stored on our own servers with enterprise-grade security</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Banking Statements</Label>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="relative" disabled={uploading}>
              <Upload className="h-4 w-4" />
              Select PDFs/CSVs
              <input
                type="file"
                className="absolute inset-0 opacity-0 cursor-pointer"
                multiple
                accept=".pdf,.csv,.xlsx,.xls"
                onChange={handleFileSelect('banking')}
                disabled={uploading}
              />
            </Button>
            <p className="text-xs text-muted-foreground">Account statements, transaction history</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Holdings & Portfolio Reports</Label>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="relative" disabled={uploading}>
              <Upload className="h-4 w-4" />
              Select Documents
              <input
                type="file"
                className="absolute inset-0 opacity-0 cursor-pointer"
                multiple
                accept=".pdf,.csv,.xlsx,.xls"
                onChange={handleFileSelect('holdings')}
                disabled={uploading}
              />
            </Button>
            <p className="text-xs text-muted-foreground">Brokerage reports, custody statements</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Investment Memos & Subscription Docs</Label>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="relative" disabled={uploading}>
              <Upload className="h-4 w-4" />
              Select Documents
              <input
                type="file"
                className="absolute inset-0 opacity-0 cursor-pointer"
                multiple
                accept=".pdf,.doc,.docx"
                onChange={handleFileSelect('memos')}
                disabled={uploading}
              />
            </Button>
            <p className="text-xs text-muted-foreground">Deal memos, subscription agreements, PPMs</p>
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          <Label>Selected Files ({files.length})</Label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {files.map((f, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{f.file.name}</p>
                  {f.uploading && (
                    <Progress value={f.progress} className="h-1 mt-1" />
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(idx)}
                  disabled={uploading}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="ghost" onClick={onSkip} disabled={uploading}>
          Skip for Now
        </Button>
        <Button onClick={uploadFiles} disabled={uploading || files.length === 0}>
          {uploading ? 'Uploading...' : `Upload ${files.length > 0 ? files.length : ''} Document${files.length !== 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
}
